import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";

import { cn } from "@/lib/utils";
import {
  ANCHORED_DETAIL_CALLOUT_Z_INDEX,
  computeAnchoredDetailPanel,
  FLOATING_COMBOBOX_PANEL_SELECTOR,
  HUB_MODAL_OVERLAY_SELECTOR,
  SEARCHABLE_SELECT_PANEL_SELECTOR,
} from "@/lib/viewportAnchoredPopover";

export const ANCHORED_DETAIL_CALLOUT_SELECTOR = "[data-anchored-detail-callout]";

/** Escape a value for use inside a CSS attribute selector. */
export function cssEscapeAttributeValue(value: string): string {
  if (typeof CSS !== "undefined" && typeof CSS.escape === "function") {
    return CSS.escape(value);
  }
  return value.replace(/[^a-zA-Z0-9_-]/g, (char) => `\\${char}`);
}

export function buildAttributeAnchorSelector(attribute: string, value: string): string {
  return `[${attribute}="${cssEscapeAttributeValue(value)}"]`;
}

export function anchoredDetailCalloutIgnoresPointerTarget(
  target: Element,
  anchorSelector: string,
  extraIgnoreSelectors: readonly string[] = [],
): boolean {
  if (target.closest(ANCHORED_DETAIL_CALLOUT_SELECTOR) != null) return true;
  if (target.closest(HUB_MODAL_OVERLAY_SELECTOR) != null) return true;
  if (target.closest(SEARCHABLE_SELECT_PANEL_SELECTOR) != null) return true;
  if (target.closest(FLOATING_COMBOBOX_PANEL_SELECTOR) != null) return true;
  if (target.closest(anchorSelector) != null) return true;
  for (const selector of extraIgnoreSelectors) {
    if (target.closest(selector) != null) return true;
  }
  return false;
}

export type AnchoredDetailCalloutProps = {
  /** Full CSS selector for the anchor element (for example `[data-foo="bar"]`). */
  anchorSelector: string;
  /** When set, positions relative to this element instead of the first `anchorSelector` match. */
  anchorElement?: HTMLElement | null;
  onClose: () => void;
  children: ReactNode;
  panelWidth: number;
  /** Minimum content height used when positioning the panel. */
  minContentHeight?: number;
  ariaLabel: string;
  /** Re-run layout measurement when this key changes. */
  measureKey?: string | number;
  className?: string;
  contentClassName?: string;
  /**
   * `smart` — scroll the shell only when content exceeds the positioned max height (clamp forecast).
   * `always` — always allow vertical scroll on the shell (forecast sourcing vendors).
   * `none` — no shell scroll; inner content owns overflow (safety stock reserve editor).
   */
  scrollMode?: "smart" | "always" | "none";
  extraDismissIgnoreSelectors?: readonly string[];
};

/**
 * Floating detail callout anchored beside a grid cell or glyph. Portaled to `document.body`,
 * recomputes on scroll/resize, dismisses on outside pointer down or Escape. Portaled combobox
 * panels (`SearchableSelect`, `SearchableMultiSelect`, `AssemblySkuCombobox`) do not dismiss the callout.
 */
export function AnchoredDetailCallout({
  anchorSelector,
  anchorElement = null,
  onClose,
  children,
  panelWidth,
  minContentHeight = 0,
  ariaLabel,
  measureKey,
  className,
  contentClassName,
  scrollMode = "smart",
  extraDismissIgnoreSelectors = [],
}: AnchoredDetailCalloutProps): JSX.Element | null {
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState<number | null>(null);
  const [box, setBox] = useState<{
    top: number;
    left: number;
    width: number;
    maxHeight: number;
    transform?: string;
  } | null>(null);

  const reposition = useCallback(() => {
    const anchor =
      anchorElement ?? document.querySelector<HTMLElement>(anchorSelector);
    if (anchor == null) {
      setBox(null);
      return;
    }
    const rect = anchor.getBoundingClientRect();
    const measuredHeight = contentRef.current?.scrollHeight ?? contentHeight;
    const layoutHeight =
      measuredHeight != null && measuredHeight > 0
        ? measuredHeight
        : Math.max(minContentHeight, measuredHeight ?? 0);
    const computed = computeAnchoredDetailPanel({
      anchorRect: rect,
      panelWidth,
      gap: 6,
      edge: 12,
      contentHeight: layoutHeight > 0 ? layoutHeight : null,
    });
    setBox({
      top: computed.top,
      left: computed.left,
      width: computed.width,
      maxHeight: computed.maxHeight,
      transform: computed.transform,
    });
  }, [anchorElement, anchorSelector, contentHeight, minContentHeight, panelWidth]);

  useLayoutEffect(() => {
    const element = contentRef.current;
    if (element == null) return;
    const measure = (): void => {
      setContentHeight(element.scrollHeight);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [children, measureKey]);

  useLayoutEffect(() => {
    reposition();
    const frame = requestAnimationFrame(reposition);
    return () => cancelAnimationFrame(frame);
  }, [reposition, contentHeight]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== "Escape") return;
      if (document.querySelector(HUB_MODAL_OVERLAY_SELECTOR) != null) return;
      onClose();
    };
    const onPointerDown = (event: PointerEvent): void => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (anchorElement != null && anchorElement.contains(target)) {
        return;
      }
      if (anchoredDetailCalloutIgnoresPointerTarget(target, anchorSelector, extraDismissIgnoreSelectors)) {
        return;
      }
      onClose();
    };
    window.addEventListener("resize", reposition);
    window.addEventListener("scroll", reposition, true);
    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onPointerDown, true);
    return () => {
      window.removeEventListener("resize", reposition);
      window.removeEventListener("scroll", reposition, true);
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onPointerDown, true);
    };
  }, [anchorElement, anchorSelector, extraDismissIgnoreSelectors, onClose, reposition]);

  if (typeof document === "undefined") return null;
  if (box == null) return null;

  const needsVerticalScroll =
    scrollMode === "smart" &&
    contentHeight != null &&
    contentHeight > box.maxHeight + 1;

  return createPortal(
    <div
      role="dialog"
      aria-label={ariaLabel}
      data-anchored-detail-callout
      style={{
        position: "fixed",
        top: box.top,
        left: box.left,
        width: box.width,
        maxHeight: box.maxHeight,
        transform: box.transform,
        zIndex: ANCHORED_DETAIL_CALLOUT_Z_INDEX,
      }}
      className={cn(
        "flex min-h-0 min-w-0 flex-col overflow-x-hidden rounded-lg border border-slate-200 bg-white shadow-2xl",
        scrollMode === "always" && "overflow-y-auto",
        scrollMode === "smart" && (needsVerticalScroll ? "overflow-y-auto" : "overflow-y-hidden"),
        scrollMode === "none" && "overflow-hidden",
        className,
      )}
    >
      <div
        ref={contentRef}
        className={cn(
          "min-w-0",
          scrollMode === "none" ? "flex min-h-0 flex-1 flex-col overflow-hidden" : "shrink-0",
          contentClassName,
        )}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
}
